import { coerceChartRowSeriesValues } from '../coerceChartRowSeriesValues';
import { lineOrBarWideRows } from '../lineOrBarWideRows';
import {
  asChartData,
  axisNumberFormat,
  chartPresentationProps,
  seriesToChartConfig,
} from '../presentation';
import type {
  ComboAdapterConfig,
  ComboAdapterProps,
  RenderAdapterResult,
} from '../types';
import { validateRowsInput } from '../validateRowsInput';

function normalizeSeriesKeys(series: readonly string[]): string[] {
  return series.filter(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  );
}

function comboProps(
  config: ComboAdapterConfig | undefined,
  data: ComboAdapterProps['data'],
): ComboAdapterProps {
  const barSeries = normalizeSeriesKeys(config?.barSeries ?? []);
  const lineSeries = normalizeSeriesKeys(config?.lineSeries ?? []);

  return {
    ...config?.presentation?.passthrough,
    data,
    barSeries,
    lineSeries,
    xAxisKey: config?.xAxisKey ?? '',
    leftYAxisFormat: axisNumberFormat(config?.presentation?.leftYAxisFormat),
    rightYAxisFormat: axisNumberFormat(config?.presentation?.rightYAxisFormat),
    // Combo's legend describes the bar and line series it draws, which can
    // diverge from the shared `series` field. Labels fall back to the series
    // key rather than reading the authored labels, which is what this chart
    // has always rendered; wiring authored labels through is a behavior change
    // and belongs in its own change.
    config: seriesToChartConfig([...barSeries, ...lineSeries]),
    ...chartPresentationProps(config?.presentation),
  };
}

export function comboAdapter(
  rows: unknown,
  config: ComboAdapterConfig,
): RenderAdapterResult<ComboAdapterProps> {
  const validated = validateRowsInput(rows, comboProps(config, []));
  if ('props' in validated) {
    return validated;
  }

  const barSeries = normalizeSeriesKeys(config.barSeries);
  const lineSeries = normalizeSeriesKeys(config.lineSeries);
  // Column preflight must cover every series the chart will draw, not only
  // `series` — the bar/line lists can diverge from that shared config field.
  const required = Array.from(
    new Set([...config.series, ...barSeries, ...lineSeries]),
  );

  const wideResult = lineOrBarWideRows(validated.rows, {
    ...config,
    series: required,
  });
  if ('error' in wideResult) {
    return { props: comboProps(config, []), error: wideResult.error };
  }

  return {
    props: comboProps(
      config,
      asChartData(
        coerceChartRowSeriesValues(wideResult.rows, [
          ...barSeries,
          ...lineSeries,
        ]),
      ),
    ),
  };
}
